/**
 * useCarouselStock — live stock overlay for Home tab carousels (DEV-46921).
 *
 * Fires one getLiveStock request per supplier for the raw-product tiles
 * shown in the recently-purchased (and optionally favourites) carousel.
 * Uses the same fan-out pattern as ProductDetail so a slow supplier
 * does not block tiles from other suppliers.
 *
 * Cache key shape: ['live-stock', supplierId, ids, postcode, lat, lon]
 * This deliberately mirrors ProductDetail's key so that if the engineer
 * loads the home tab then immediately opens a product detail, the detail
 * page reads from cache rather than firing a duplicate request.
 *
 * staleTime: 5 minutes — balances freshness against unnecessary calls on
 * a page that is re-visited often during a job.
 */

import { useMemo } from 'react'
import { useQueries } from '@tanstack/react-query'
import { api } from '../../services/api'
import type { BestListing, StockStatus } from '../../types'

interface Session {
  postcode: string
  latitude: number
  longitude: number
}

const CAROUSEL_STOCK_STALE_MS = 5 * 60 * 1000 // 5 minutes

/**
 * Given a flat list of BestListing tiles, fires one live-stock call per
 * supplier (grouped by supplierId × rawProductId) and returns a Map of
 * rawProductId → live StockStatus.
 *
 * While any supplier's request is still in-flight the rawProductId for
 * that supplier is absent from the map — callers fall back to the cached
 * listing.stockStatus so the tile shows its last-known state rather than
 * flipping to 'checking'.
 *
 * Only raw-target listings (targetType === 'raw') carry a rawProductId
 * usable with getLiveStock; master-target listings (favourites opened
 * from search) are skipped.
 */
export function useCarouselStock(
  listings: BestListing[],
  session: Session | null,
): Map<number, StockStatus> {
  // Group rawProductIds by supplierId.
  const supplierGroups = useMemo(() => {
    const map = new Map<number, number[]>()
    for (const listing of listings) {
      if (listing.rawProductId == null || listing.supplierId == null) continue
      const suppId = Number(listing.supplierId)
      if (!Number.isFinite(suppId)) continue
      if (!map.has(suppId)) map.set(suppId, [])
      map.get(suppId)!.push(listing.rawProductId)
    }
    return map
  }, [listings])

  // Stable sorted list so useQueries receives a consistent array length
  // (adding/removing suppliers doesn't reorder existing slots).
  const supplierIds = useMemo(
    () => Array.from(supplierGroups.keys()).sort((a, b) => a - b),
    [supplierGroups],
  )

  const stockQueries = useQueries({
    queries: supplierIds.map((supplierId) => {
      const ids = supplierGroups.get(supplierId) ?? []
      return {
        queryKey: [
          'live-stock',
          supplierId,
          // Sort ids so the key is stable regardless of listing order.
          [...ids].sort((a, b) => a - b),
          session?.postcode,
          session?.latitude,
          session?.longitude,
        ],
        queryFn: () =>
          api.getLiveStock(
            ids,
            session!.postcode,
            session!.latitude,
            session!.longitude,
            supplierId,
          ),
        enabled: !!session?.postcode && ids.length > 0,
        staleTime: CAROUSEL_STOCK_STALE_MS,
      }
    }),
  })

  return useMemo(() => {
    const statusMap = new Map<number, StockStatus>()
    supplierIds.forEach((supplierId, idx) => {
      const q = stockQueries[idx]
      const ids = supplierGroups.get(supplierId) ?? []

      // Supplier query still loading — leave those rawProductIds absent
      // from the map so callers show the last-known status (no flash).
      if (q.isLoading) return

      // Supplier query errored entirely — mark all its products as
      // 'checking' so callers don't fall back to a stale cached status
      // that may say "in_stock" when the product is actually unavailable.
      if (q.isError || !Array.isArray(q.data?.stock)) {
        for (const rawId of ids) {
          statusMap.set(rawId, 'checking')
        }
        return
      }

      // Track which rawProductIds got a definitive answer from the API.
      const answered = new Set<number>()

      for (const stockItem of q.data.stock as Array<Record<string, unknown>>) {
        const rawId = Number(stockItem.raw_product_id)
        if (!Number.isFinite(rawId)) continue
        answered.add(rawId)

        // A branch row confirms availability when the supplier reports
        // collection-, delivery-, or DC-stock — the exact rule the search and
        // recently-purchased list use (collection OR delivery OR dc > 0). This
        // is deliberately NOT the branch-only stock_quantity: a DC-only item
        // (e.g. CPS, branch empty but stock in the DC) is genuinely available
        // for delivery, so it must read in stock rather than out of stock.
        // error / stock_unavailable rows mean stock could not be confirmed.
        const rowInStock =
          !stockItem.error &&
          !stockItem.stock_unavailable &&
          (Number(stockItem.collection_stock ?? 0) > 0 ||
            Number(stockItem.delivery_stock ?? 0) > 0 ||
            Number(stockItem.dc_stock ?? 0) > 0)

        // The endpoint returns up to 3 nearest-branch rows per product, so
        // aggregate as "in stock if ANY row is" — once a row confirms stock,
        // a later (farther) empty branch row must not downgrade it.
        if (rowInStock) {
          statusMap.set(rawId, 'in_stock')
        } else if (statusMap.get(rawId) !== 'in_stock') {
          statusMap.set(rawId, 'out_of_stock')
        }
      }

      // Products we asked about but the API didn't include in its
      // response — treat as out_of_stock rather than falling back to
      // a potentially stale cached status.
      for (const rawId of ids) {
        if (!answered.has(rawId)) {
          statusMap.set(rawId, 'out_of_stock')
        }
      }
    })
    return statusMap
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierIds, supplierGroups, stockQueries])
}
